import {getTestProps} from '@benchling/js-test-utils';
import {Checkbox, Chip, Input, Stack, Text, TextInput} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useRef, useState} from 'react';

import {refreshAnalysisTemplate} from 'analysis-tool/polling/refreshPipeline';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {
  GetDatasetColumns,
  GetDatasetColumnsVariables,
} from 'data-frames/graphql/__generated/GetDatasetColumns';
import {GET_DATASET_COLUMNS_QUERY} from 'data-frames/graphql/GetDataFrame';
import {createDataFrame} from 'data-frames/network/DataFrameDAO';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import notifications from 'notifications';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import type {CreateStepParams} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStepConfig} from 'pipelines/PipelineTypes';
import {getInputStepDatas, getOutputStepDatas} from 'pipelines/StepConnectionUtils';
import SearchInput from 'search/inputs/SearchInput';
import SelectableCard from 'ui-library/inputs/SelectableCard';
import {withLoadingScreen} from 'util/Promises';

import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
  RightSidebarComponentProps,
} from '../../RightSidebarTypes';
import RightSidebarSkeleton, {RightSidebarWidth} from '../RightSidebarSkeleton';

enum TemplateInputType {
  VARIABLE = 'VARIABLE',
  STATIC = 'STATIC',
}

// Mantine only adds a 2px gap between a description that sits above the input and the input itself. Give the
// help text 6px of breathing room to match the design.
const FIELD_STYLES = {description: {marginBottom: 6}} as const;

/**
 * Build `CreateStepParams` to add a static input table.
 *
 * A static input table is represented by a TEMPLATE_INPUT step.
 *
 * TEMPLATE_INPUT steps are not executable; instead their output data frame is directly patched in.
 */
async function getStaticInputTableStepsToAdd(
  datasetId: string,
  name: string
): Promise<Array<CreateStepParams>> {
  // Copy the data frame which backs the selected dataset, so it can be independently patched into the
  // TEMPLATE_INPUT step.
  const {createdIds} = await createDataFrame({name, datasetId});
  assert(createdIds.length === 1, 'Expected exactly one created data frame');
  const [outputStepData] = getOutputStepDatas(PipelineStepType.TEMPLATE_INPUT);

  return [
    {
      stepType: PipelineStepType.TEMPLATE_INPUT,
      stepVersion: 1,
      config: undefined,
      outputStepDatas: [{name: outputStepData.systemName, rawDataIds: [createdIds[0]]}],
      runAfterCreate: false, // The TEMPLATE_INPUT step is not run; its outputs are directly patched in.
    },
  ];
}

/**
 * Build `CreateStepParams` to add a variable input table.
 *
 * A variable input table is represented by a VARIABLE_TEMPLATE_INPUT_TABLE step type.
 *
 * VARIABLE_TEMPLATE_INPUT_TABLE steps behave like a normal step type, except:
 * - VARIABLE_TEMPLATE_INPUT_TABLE steps are synced with `AnalysisTemplateDataFrameVariable` models.
 * - When applying the template, the VARIABLE_TEMPLATE_INPUT_TABLE step will be dropeed.
 *
 * If the user or integration is not required to provide a column mapping (`requireColumnMapping`), an
 * `APPLY_COLUMN_MAPPING` step is added, so the template builder can define upfront how varying columns will
 * be handled.
 */
async function getVariableInputTableStepsToAdd(
  datasetId: string,
  name: string,
  description: string,
  requireColumnMapping: boolean
): Promise<Array<CreateStepParams>> {
  const [outputStepData] = getOutputStepDatas(PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE);
  const [inputStepData] = getInputStepDatas(PipelineStepType.APPLY_COLUMN_MAPPING);

  const stepsToAdd: Array<CreateStepParams> = [
    {
      stepType: PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE,
      stepVersion: 0,
      config: {
        datasetId,
        name,
        description,
        requireColumnMapping,
      },
      incomingEdges: [],
      runAfterCreate: true,
    },
  ];

  if (!requireColumnMapping) {
    const {dataset} = await doQuerySafe<GetDatasetColumns, GetDatasetColumnsVariables>(
      {query: GET_DATASET_COLUMNS_QUERY},
      {variables: {datasetId}}
    );

    if (
      dataset.__typename === 'InaccessibleInfoNode' ||
      dataset.dataFrame.__typename === 'InaccessibleInfoNode'
    ) {
      notifications.error('The selected dataset could not be loaded.');
      return [];
    }

    const outputColumns: PipelineStepConfig<PipelineStepType.APPLY_COLUMN_MAPPING, 0>['outputColumns'] = (
      dataset.dataFrame.allColumnsJson as Array<DataFrameColumnDef>
    ).map((column) => ({
      matchBy: 'NAME' as const,
      caseSensitive: true,
      displayName: column.displayName,
      systemName: column.systemName,
      createEmptyColumnIfNotFound: false,
    }));

    stepsToAdd.push({
      stepType: PipelineStepType.APPLY_COLUMN_MAPPING,
      stepVersion: 0,
      config: {keepUnmappedColumns: true, outputColumns},
      incomingEdges: [
        {
          fromStepIndex: 0,
          fromStepDataName: outputStepData.systemName,
          fromStepDataIndex: null,
          toStepDataName: inputStepData.systemName,
          toStepDataIndex: 0,
        },
      ],
    });
  }

  return stepsToAdd;
}

async function addTemplateInputStepAndProgressRightSidebar({
  analysis,
  datasetId,
  name,
  description,
  isVariable,
  requireColumnMapping,
  onChangeSelectedRightSidebarItem,
}: {
  analysis: Pick<PipelineGraphQL, 'id'>;
  datasetId: string;
  name: string;
  description: string;
  isVariable: boolean;
  requireColumnMapping: boolean;
  onChangeSelectedRightSidebarItem: (item: AnalysisRightSidebarItem | null) => void;
}): Promise<void> {
  await withLoadingScreen(
    (async () => {
      let stepsToAdd: Array<CreateStepParams>;

      if (isVariable) {
        stepsToAdd = await getVariableInputTableStepsToAdd(
          datasetId,
          name,
          description,
          requireColumnMapping
        );
      } else {
        stepsToAdd = await getStaticInputTableStepsToAdd(datasetId, name);
      }

      if (stepsToAdd.length === 0) {
        // User was already notified of any errors.
        return;
      }

      await PipelineDAO.addAndUpdatePipelineSteps(analysis.id, {
        stepsToAdd,
        stepsToUpdate: [],
      });
      await refreshAnalysisTemplate(analysis.id);

      // TODO(BNCH-165691): Progress user to the APPLY_COLUMN_MAPPING step
      onChangeSelectedRightSidebarItem(null);
    })()
  );
}

/**
 * Sub-panel reached from the source step picker when a user picks "Create table from dataset" while editing an
 * analysis template. Lets the user select an existing dataset and choose whether the input is variable or
 * static, then creates a `TEMPLATE_INPUT` step whose output is a data frame copied from that dataset. The
 * backend derives a data frame variable from the step's `isVariable` config.
 */
export default function AddTemplateInputSourceRightSidebar({
  analysis,
  onChangeSelectedRightSidebarItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.ADD_TEMPLATE_INPUT_SOURCE>): JSX.Element {
  const datasetInputId = useId();
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [inputType, setInputType] = useState<TemplateInputType>(TemplateInputType.VARIABLE);
  const [requireColumnMapping, setRequireColumnMapping] = useState<boolean>(false);
  const [description, setDescription] = useState('');
  const [isFinalizing, setIsFinalizing] = useState(false);
  // `isFinalizing` state doesn't update synchronously, so a fast double-click could enter `onSave` twice
  // before the rerender. This ref guards against that, so only one save runs at a time.
  const isSavingRef = useRef(false);

  const isVariable = inputType === TemplateInputType.VARIABLE;
  const canFinalize = selectedDatasetId != null && name.trim() !== '';

  const onSave = async (): Promise<void> => {
    if (!canFinalize || selectedDatasetId == null || isSavingRef.current) {
      return;
    }
    try {
      isSavingRef.current = true;
      setIsFinalizing(true);
      const trimmedName = name.trim();
      const trimmedDescription = description.trim();
      await addTemplateInputStepAndProgressRightSidebar({
        analysis,
        datasetId: selectedDatasetId,
        name: trimmedName,
        description: trimmedDescription,
        isVariable,
        requireColumnMapping,
        onChangeSelectedRightSidebarItem,
      });
    } finally {
      setIsFinalizing(false);
      isSavingRef.current = false;
    }
  };

  return (
    <RightSidebarSkeleton
      title="Create table from dataset"
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: () => {
            onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.ADD_SOURCE_STEP});
          },
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Save',
          onClick: onSave,
          dataTestAttrsElement: 'save',
          disabled: !canFinalize || isFinalizing,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.MEDIUM}
      dataTestAttrs={{
        component: 'AddTemplateInputSourceRightSidebar',
        element: 'root',
      }}
    >
      <Stack spacing="md">
        <div>
          <Text size="md" weight={500} mb="xs">
            Create table with:
          </Text>
          <Chip.Group
            multiple={false}
            value={inputType}
            onChange={(value) => {
              if (value) {
                setInputType(value as TemplateInputType);
              }
            }}
          >
            <Stack spacing="sm">
              <SelectableCard
                value={TemplateInputType.VARIABLE}
                title="Variable data"
                description="Dataset provided when the template is applied"
                dataTestAttrs={{
                  component: 'AddTemplateInputSourceRightSidebar',
                  element: 'variableOption',
                }}
              />
              <SelectableCard
                value={TemplateInputType.STATIC}
                title="Static data"
                description="Same dataset each time the template is applied"
                dataTestAttrs={{
                  component: 'AddTemplateInputSourceRightSidebar',
                  element: 'staticOption',
                }}
              />
            </Stack>
          </Chip.Group>
        </div>
        <Input.Wrapper
          id={datasetInputId}
          label={isVariable ? 'Reference dataset' : 'Dataset'}
          description={
            isVariable
              ? 'Used to test the analysis as you design the template. Users map to these columns when applying the template.'
              : undefined
          }
          withAsterisk={true}
          inputWrapperOrder={['label', 'description', 'input']}
          styles={FIELD_STYLES}
        >
          <SearchInput
            id={datasetInputId}
            autoFocus={false}
            disableTyping={selectedDatasetId != null}
            isMulti={false}
            onRemove={() => {
              setSelectedDatasetId(null);
            }}
            onSelect={(id) => {
              setSelectedDatasetId(id);
            }}
            placeholder=""
            selected={selectedDatasetId == null ? [] : [selectedDatasetId]}
            types={[ModelTypeConstants.DATASET]}
          />
        </Input.Wrapper>
        {isVariable && (
          <Checkbox
            label="Require user to map columns when applying template"
            checked={requireColumnMapping}
            onChange={(e) => {
              setRequireColumnMapping(e.currentTarget.checked);
            }}
            {...getTestProps({
              component: 'AddTemplateInputSourceRightSidebar',
              element: 'requireColumnMappingCheckbox',
            })}
          />
        )}
        {isVariable && (
          <TextInput
            label="Description"
            description="Describe what data is expected so users know what to provide"
            inputWrapperOrder={['label', 'description', 'input']}
            styles={FIELD_STYLES}
            placeholder="e.g. Select a dataset with columns for..."
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            {...getTestProps({
              component: 'AddTemplateInputSourceRightSidebar',
              element: 'descriptionInput',
            })}
          />
        )}
        <TextInput
          label="Table name"
          withAsterisk={true}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          {...getTestProps({
            component: 'AddTemplateInputSourceRightSidebar',
            element: 'nameInput',
          })}
        />
      </Stack>
    </RightSidebarSkeleton>
  );
}
